<script setup></script>

<template>
  <v-container class="mt-16">
    <v-row>
      <v-col cols="12" md="8" class="mx-auto">
        <v-card class="pa-8 rounded-lg elevation-2">
          <v-card-title class="justify-center text-h4 font-weight-bold">
            Integritetspolicy
          </v-card-title>
          <v-divider class="my-4"></v-divider>

          <v-card-text>
            <p class="text-subtitle-2 mb-4">Senast uppdaterad: 2025-09-25</p>

            <p>
              Vi värnar om din personliga integritet. Denna integritetspolicy
              förklarar hur vi samlar in, använder och skyddar dina
              personuppgifter i samband med vår mässplattform.
            </p>

            <!-- Personuppgifter -->
            <h2 class="text-h5 mt-6 mb-2">Personuppgifter vi behandlar</h2>
            <p>
              Vi behandlar följande uppgifter om dig när du använder vår tjänst:
            </p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="primary">mdi-account</v-icon>
                Namn, e-postadress och inloggningsuppgifter
              </v-list-item>
              <v-list-item>
                <v-icon start color="primary">mdi-school</v-icon>
                Utbildning, profilinformation och intresseanmälningar
              </v-list-item>
              <v-list-item>
                <v-icon start color="primary">mdi-email</v-icon>
                Ansökningar om kontaktsamtal
              </v-list-item>
              <v-list-item>
                <v-icon start color="primary">mdi-office-building</v-icon>
                För företagsrepresentanter: namn, arbetsgivare och
                kontaktuppgifter
              </v-list-item>
            </v-list>

            <v-divider class="my-6"></v-divider>

            <!-- Ändamål -->
            <h2 class="text-h5 mb-2">Ändamål och rättslig grund</h2>
            <p>Vi behandlar dina uppgifter för att:</p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="success">mdi-check</v-icon>
                Ge dig tillgång till vårt användarkonto (avtal)
              </v-list-item>
              <v-list-item>
                <v-icon start color="success">mdi-check</v-icon>
                Möjliggöra interaktion mellan studenter och företag (avtal)
              </v-list-item>
              <v-list-item>
                <v-icon start color="success">mdi-check</v-icon>
                Administrera och utveckla tjänsten (berättigat intresse)
              </v-list-item>
              <v-list-item>
                <v-icon start color="success">mdi-check</v-icon>
                Uppfylla rättsliga skyldigheter (rättslig förpliktelse)
              </v-list-item>
            </v-list>

            <!-- Lagringstid -->
            <h2 class="text-h5 mt-6 mb-2">Lagringstid</h2>
            <p>
              Vi sparar dina uppgifter så länge du har ett konto. Du kan begära
              radering när som helst. Därefter raderas uppgifter inom 30 dagar.
            </p>

            <!-- Delning -->
            <h2 class="text-h5 mt-6 mb-2">Delning av uppgifter</h2>
            <p>Dina uppgifter delas endast med:</p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="warning">mdi-share</v-icon>
                Företagsrepresentanter vid intresseanmälan/ansökan
              </v-list-item>
              <v-list-item>
                <v-icon start color="warning">mdi-server</v-icon>
                Tekniska tjänsteleverantörer (t.ex. drift och lagring)
              </v-list-item>
              <v-list-item>
                <v-icon start color="warning">mdi-gavel</v-icon>
                Myndigheter om vi är skyldiga enligt lag
              </v-list-item>
            </v-list>

            <!-- Dina rättigheter -->
            <h2 class="text-h5 mt-6 mb-2">Dina rättigheter</h2>
            <p>Du har rätt att:</p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="info">mdi-file-document</v-icon>
                Få tillgång till dina uppgifter (registerutdrag)
              </v-list-item>
              <v-list-item>
                <v-icon start color="info">mdi-pencil</v-icon>
                Begära rättelse eller radering
              </v-list-item>
              <v-list-item>
                <v-icon start color="info">mdi-cancel</v-icon>
                Invända mot viss behandling
              </v-list-item>
              <v-list-item>
                <v-icon start color="info">mdi-database-export</v-icon>
                Begära dataportabilitet
              </v-list-item>
            </v-list>

            <p class="mt-4">
              För att utöva dina rättigheter, kontakta oss på:
              <a href="mailto:ordforande@sofre.se">ordforande@sofre.se</a>.
            </p>

            <!-- Personuppgiftsansvarig -->
            <h2 class="text-h5 mt-6 mb-2">Personuppgiftsansvarig</h2>
            <p>Södertörns Förenade Ekonomer</p>
            <p>802408-7523</p>
            <p>Alfred Nobels Allé 9, 141 52 Huddinge</p>
            <p>E-post: ordforande@sofre.se</p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="8" class="mx-auto">
        <v-card class="pa-8 rounded-lg elevation-2">
          <v-card-title class="justify-center text-h4 font-weight-bold">
            Användarvillkor
          </v-card-title>
          <v-divider class="my-4"></v-divider>

          <v-card-text>
            <p class="text-subtitle-2 mb-4">Senast uppdaterad: 2025-09-25</p>

            <p>
              Genom att skapa ett konto på
              <strong>e-day kontaktsamtal</strong> godkänner du dessa villkor.
            </p>

            <!-- 1. Syfte -->
            <h2 class="text-h5 mt-6 mb-2">1. Syfte</h2>
            <p>
              e-day kontaktsamtal är en digital plattform för e-day där
              studenter kan presentera sig för företag och ansöka om
              kontaktsamtal.
            </p>

            <!-- 2. Konto -->
            <h2 class="text-h5 mt-6 mb-2">2. Konto</h2>
            <p>
              För att använda tjänsten behöver du skapa ett konto. Du ansvarar
              för att:
            </p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="primary">mdi-account-check</v-icon>
                Lämna korrekta uppgifter
              </v-list-item>
              <v-list-item>
                <v-icon start color="primary">mdi-lock</v-icon>
                Skydda dina inloggningsuppgifter
              </v-list-item>
              <v-list-item>
                <v-icon start color="primary">mdi-account-off</v-icon>
                Inte överlåta ditt konto till någon annan
              </v-list-item>
            </v-list>

            <!-- 3. Användning -->
            <h2 class="text-h5 mt-6 mb-2">3. Användning</h2>
            <p>
              Du får endast använda tjänsten i enlighet med dess syfte. Det är
              förbjudet att:
            </p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="error">mdi-alert</v-icon>
                Skapa falska profiler
              </v-list-item>
              <v-list-item>
                <v-icon start color="error">mdi-comment-alert</v-icon>
                Lämna kränkande, olämpligt eller olagligt innehåll
              </v-list-item>
              <v-list-item>
                <v-icon start color="error">mdi-shield-off</v-icon>
                Försöka kringgå säkerhetssystem
              </v-list-item>
            </v-list>

            <!-- 4. Ansvarsbegränsning -->
            <h2 class="text-h5 mt-6 mb-2">4. Ansvarsbegränsning</h2>
            <p>e-day kontaktsamtal ansvarar inte för:</p>
            <v-list density="compact">
              <v-list-item>
                <v-icon start color="grey">mdi-account-question</v-icon>
                Innehållet i användarprofiler
              </v-list-item>
              <v-list-item>
                <v-icon start color="grey">mdi-handshake</v-icon>
                Eventuella överenskommelser mellan studenter och företag
              </v-list-item>
              <v-list-item>
                <v-icon start color="grey">mdi-server-off</v-icon>
                Skador som uppstår på grund av avbrott, tekniska problem eller
                obehörig åtkomst
              </v-list-item>
            </v-list>

            <!-- 5. Avstängning -->
            <h2 class="text-h5 mt-6 mb-2">5. Avstängning</h2>
            <p>
              Vi förbehåller oss rätten att stänga av eller radera konton som
              bryter mot villkoren.
            </p>

            <!-- 6. Tillämplig lag -->
            <h2 class="text-h5 mt-6 mb-2">6. Tillämplig lag</h2>
            <p>
              Dessa villkor regleras av svensk lag. Tvister avgörs av svensk
              domstol.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
